import imageCompression from 'browser-image-compression';

function isAbortError(err: unknown): boolean {
  return !!err && typeof err === "object" && "name" in (err as any) && (err as any).name === "AbortError";
}

async function showSaveFilePickerSafe(options?: SaveFilePickerOptions) {
  try {
    if (!("showSaveFilePicker" in window)) return undefined;
    return await (window as unknown as { showSaveFilePicker: SavePicker }).showSaveFilePicker(options);
  } catch (err) {
    if (isAbortError(err)) return undefined; // user cancelled — do nothing
    throw err; // real error — bubble up
  }
}

type SavePicker = (options?: any) => Promise<FileSystemFileHandle>;

export async function saveFileWithDialog(file: File) {
  const canPick =
    typeof window !== "undefined" &&
    "showSaveFilePicker" in window &&
    typeof (window as any).showSaveFilePicker === "function";

  if (canPick) {
    const mime: any = file.type || "application/octet-stream";
    try {
      const handle = await showSaveFilePickerSafe({
        suggestedName: file.name || "download",
        excludeAcceptAllOption: false,
        types: [{ description: "File", accept: { [mime]: [".png", ".jpg", ".jpeg", ".webp", ".gif", ".bin"] } }],
      });
  
      if (!handle) return; // cancelled
  
      const writable = await handle.createWritable();
      await writable.write(file);
      await writable.close();
    } catch (err) {
      if (isAbortError(err)) return; // extra safety
      // TODO: show a toast or log
      console.error(err);
    }
    return;
  }

  // Fallback: download via object URL to default Downloads folder
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Utility: get filename from Content-Disposition if present
export function filenameFromContentDisposition(h: string | null): string | null {
  if (!h) return null;
  // RFC 5987 / 6266 handling (very light)
  const mStar = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(h);
  if (mStar) return decodeURIComponent(mStar[2].trim());

  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(h);
  return m ? m[1].trim() : null;
}

/**
 * Fetches a file from a URL and automatically fixes
 * EXIF orientation for JPEG images.
 */
export async function urlToFile(
  signedUrl: string,
  defaultFilename = "file.bin"
): Promise<File> {
  // 1. Fetch the file as before
  const res = await fetch(signedUrl, { method: "GET", mode: "cors", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition");
  const serverName = filenameFromContentDisposition(cd);
  const filename = serverName || defaultFilename;

  const type = blob.type || res.headers.get("Content-Type") || "application/octet-stream";
  
  // 2. Create the initial File object
  const originalFile = new File([blob], filename, { type: typeof type === "string" ? type : undefined });

  // --- START ORIENTATION FIX ---

  // 3. Check if it's a JPEG. If not, return it directly.
  if (originalFile.type !== 'image/jpeg') {
    return originalFile;
  }
  
  // 4. If it IS a JPEG, process it to fix orientation
  try {
    const options = {
      maxSizeMB: 10,
    };

    const fixedFile = await imageCompression(originalFile, options);
    
    // 5. Return the new, fixed File
    return fixedFile;

  } catch (error) {
    console.error('Error fixing image orientation, returning original file.', error);
    // If fixing fails, return the original file to avoid breaking the flow
    return originalFile;
  }
  
  // --- END ORIENTATION FIX ---
}

export async function downloadFromSignedUrl(signedUrl: string, fallbackName = "download.bin") {
  const file = await urlToFile(signedUrl, fallbackName);
  await saveFileWithDialog(file);
}
